import { useState, type ReactNode } from 'react';
import type { Person } from '@domain/entities/Person';
import { PERSON_DOCUMENTS, type PersonDocumentKind } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import { Banner, Card, cx } from '../../design-system';
import { DocumentViewer, type DocumentoVisible } from '../../documents/DocumentViewer';
import { nombreDescarga } from '../../documents/nombreDescarga';
import { formatDayWithYear } from '../../format';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { useAccess } from '../../hooks/useAccess';
import { useToast } from '../../providers/ToastProvider';
import styles from './PersonDocumentsCard.module.css';

/**
 * El expediente de contratación: los ocho papeles y cuáles faltan.
 *
 * **Detrás de `ver_expediente`, y la tarjeta entera desaparece sin él.** No se
 * enseña una lista de títulos con los enlaces apagados: saber que existe el acta
 * de nacimiento de alguien ya es información, y esta tarjeta convive con la ficha
 * que sí puede leer un líder.
 *
 * Se guarda la **ruta** de Storage y la URL se pide al abrir. Las de descarga
 * caducan: una ficha que guardara una se quedaría con un enlace muerto y sin
 * forma de notarlo hasta que alguien lo pulsara.
 */
export const PersonDocumentsCard = ({
  person,
  onDone,
}: {
  readonly person: Person;
  readonly onDone: () => Promise<void>;
}): ReactNode => {
  const { holds } = useAccess();
  const toast = useToast();
  const [subiendo, setSubiendo] = useState<PersonDocumentKind | null>(null);
  const [viendo, setViendo] = useState<number | null>(null);

  if (!holds('ver_expediente')) return null;

  const puedeSubir = holds('editar_expediente');
  const faltan = PERSON_DOCUMENTS.filter(
    (doc) => doc.required && person.documents[doc.key] === undefined,
  ).length;

  /*
   * Los papeles que esta persona sí tiene, en el orden de la lista.
   *
   * Se le pasan enteros al visor y no de uno en uno: revisar un expediente es
   * mirar varios seguidos, y abrir el acta para tener que cerrarla, buscar la
   * CURP y volver a abrir era el trabajo que hacía la pestaña nueva.
   */
  const guardados: readonly DocumentoVisible[] = PERSON_DOCUMENTS.flatMap((doc) => {
    const guardado = person.documents[doc.key];
    return guardado === undefined
      ? []
      : [
          {
            titulo: doc.label,
            path: guardado.storagePath,
            nombreArchivo: nombreDescarga(doc.label, person.displayName),
          },
        ];
  });

  const subir = async (kind: PersonDocumentKind, file: File): Promise<void> => {
    setSubiendo(kind);

    const subida = await getServices().documents.uploadPersonDocument({
      file,
      fileName: file.name,
      ownerPersonId: person.id,
      areaId: person.areaId,
      recordId: kind,
    });

    if (!subida.ok) {
      setSubiendo(null);
      toast.bad(
        subida.error.kind === 'wrong-type'
          ? 'Sólo PDF.'
          : subida.error.kind === 'too-large'
            ? 'El archivo pesa demasiado.'
            : 'No se pudo subir el documento.',
      );
      return;
    }

    /* Los bytes ya están en el bucket; esto es lo que deja constancia de que
       existen. Si falla, el archivo queda huérfano y hay que volver a subirlo —
       se dice, en vez de fingir que se guardó. */
    const enlace = await getServices().people.attachDocument({
      personId: person.id,
      kind,
      storagePath: subida.value,
    });
    setSubiendo(null);

    if (!enlace.ok) {
      toast.bad('El archivo se subió pero no quedó anotado en la ficha. Inténtalo otra vez.');
      return;
    }

    toast.ok('Documento agregado al expediente.');
    await onDone();
  };

  return (
    <Card>
      <div className={styles.head}>
        <h2 className={styles.title}>Documentos</h2>
        <span className={cx(styles.count, faltan === 0 && styles.countDone)}>
          {faltan === 0 ? 'Completo' : `Faltan ${String(faltan)}`}
        </span>
      </div>

      {!puedeSubir && (
        <Banner tone="info">
          Puedes verlos. Para agregar o reemplazar alguno hace falta el permiso de corregir
          el expediente.
        </Banner>
      )}

      <ul className={styles.list}>
        {PERSON_DOCUMENTS.map((doc) => {
          const guardado = person.documents[doc.key];
          const cargando = subiendo === doc.key;

          return (
            <li key={doc.key} className={cx(styles.row, guardado !== undefined && styles.rowReady)}>
              <span className={styles.mark} aria-hidden="true">
                {guardado === undefined ? '·' : '✓'}
              </span>

              <span className={styles.text}>
                <span className={styles.name}>
                  {doc.label}
                  {!doc.required && <span className={styles.optional}> · opcional</span>}
                </span>
                <span className={styles.note}>
                  {guardado === undefined
                    ? 'Falta'
                    : guardado.uploadedAt === null
                      ? 'Subido'
                      : `Subido el ${formatDayWithYear(CalendarDate.fromInstantLocal(guardado.uploadedAt))}`}
                </span>
              </span>

              {guardado !== undefined && (
                <button
                  type="button"
                  className={styles.open}
                  onClick={() => {
                    setViendo(guardados.findIndex((item) => item.path === guardado.storagePath));
                  }}
                >
                  Abrir
                </button>
              )}

              {puedeSubir && (
                <label className={styles.upload}>
                  {cargando ? 'Subiendo…' : guardado === undefined ? 'Subir' : 'Reemplazar'}
                  <input
                    type="file"
                    accept="application/pdf"
                    className={styles.uploadInput}
                    disabled={cargando}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (file !== undefined) void subir(doc.key, file);
                    }}
                  />
                </label>
              )}
            </li>
          );
        })}
      </ul>

      {viendo !== null && viendo >= 0 && (
        <DocumentViewer
          documentos={guardados}
          inicial={viendo}
          subtitulo={person.displayName}
          onClose={() => {
            setViendo(null);
          }}
        />
      )}
    </Card>
  );
};
